/**
 * ReferenceFieldCatalog — экран ведения полевого справочника: состояния поля
 * и типы угодий.
 *
 * Тот же паттерн, что `ReferenceMiniCatalog` (категории болезней): шапка +
 * `Card` + `Table`, создание отдельной строкой сверху, правка — inline. Отдельный
 * компонент, а не проп у мини-каталога, потому что правила здесь другие и
 * протаскивать их флагами в общий компонент значило бы менять поведение шести
 * уже работающих экранов:
 *
 * · **Удаления нет.** Строку убирают в архив и возвращают обратно — на неё
 *   ссылаются посевы и поля (десятки тысяч строк), и стереть её значило бы
 *   потерять подпись у этих данных. «Убрать в архив» — это и есть «удалить» на
 *   языке экрана.
 * · **Код не редактируется.** После создания он показывается серым и только
 *   читается: по коду сервер узнаёт пар, пашню и АМП.
 * · **Свои поля:** цвет, порядок в списке и (у угодий) признак севооборота.
 *
 * Записи, на которые опирается сама система, приходят с `is_system` — у них
 * замок вместо кнопки архивации.
 */
import { useState } from 'react';
import { Archive, ArchiveRestore, Check, Lock, Pencil, Plus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface FieldCatalogRow {
  id: string;
  code: string;
  name_translations: Record<string, string>;
  color: string | null;
  sort_order: number;
  is_active: boolean;
  /** Сколько посевов / полей ссылаются на запись во всех хозяйствах. */
  usage_count: number;
  /** Запись нужна самой системе — архивировать нельзя. */
  is_system: boolean;
  /** Только у типов угодий (ADR-046: полный севооборот гоняет только пашня). */
  runs_crop_rotation?: boolean;
}

export interface FieldCatalogCreatePayload {
  code: string;
  name_translations: Record<string, string>;
  color: string | null;
  sort_order: number;
  runs_crop_rotation?: boolean;
}

export type FieldCatalogUpdatePayload = Omit<FieldCatalogCreatePayload, 'code'>;

export interface ReferenceFieldCatalogProps {
  title: string;
  subtitle?: string;
  createLabel: string;
  /** «3 поля» / «31 000 посевов» — склонение считает вызывающая страница. */
  usageUnit: (n: number) => string;
  /** Показывать колонку «Севооборот» (только для типов угодий). */
  withRotation?: boolean;

  items: FieldCatalogRow[];
  isLoading: boolean;

  onCreate: (payload: FieldCatalogCreatePayload) => Promise<void>;
  onUpdate: (id: string, payload: FieldCatalogUpdatePayload) => Promise<void>;
  onArchive: (id: string) => Promise<void>;
  onRestore: (id: string) => Promise<void>;
}

interface RowDraft {
  code: string;
  ru: string;
  color: string;
  sortOrder: string;
  rotation: boolean;
}

/** Латиница в нижнем регистре — ровно то, что примет сервер. */
export function normalizeCatalogCode(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9_]/g, '');
}

/** Пусто — тоже валидно: цвет необязателен. */
export function isValidColor(value: string): boolean {
  return value.trim() === '' || /^#[0-9a-fA-F]{6}$/.test(value.trim());
}

export function draftValid(d: RowDraft, needCode: boolean): boolean {
  if (d.ru.trim().length === 0) return false;
  if (needCode && !/^[a-z][a-z0-9_]*$/.test(d.code)) return false;
  if (!isValidColor(d.color)) return false;
  return d.sortOrder.trim() === '' || Number.isFinite(Number(d.sortOrder));
}

function emptyDraft(): RowDraft {
  return { code: '', ru: '', color: '', sortOrder: '0', rotation: false };
}

function rowToDraft(it: FieldCatalogRow): RowDraft {
  return {
    code: it.code,
    ru: it.name_translations.ru ?? '',
    color: it.color ?? '',
    sortOrder: String(it.sort_order),
    rotation: it.runs_crop_rotation ?? false,
  };
}

export function draftToPayload(d: RowDraft, withRotation: boolean): FieldCatalogCreatePayload {
  return {
    code: d.code.trim(),
    name_translations: { ru: d.ru.trim() },
    color: d.color.trim() === '' ? null : d.color.trim(),
    sort_order: d.sortOrder.trim() === '' ? 0 : Number(d.sortOrder),
    ...(withRotation ? { runs_crop_rotation: d.rotation } : {}),
  };
}

function ColorSwatch({ color }: { color: string | null }) {
  if (!color) return <span className="text-sm text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className="inline-block h-4 w-4 rounded border border-border"
        style={{ backgroundColor: color }}
      />
      <span className="font-mono text-xs text-muted-foreground">{color}</span>
    </span>
  );
}

export function ReferenceFieldCatalog({
  title,
  subtitle,
  createLabel,
  usageUnit,
  withRotation = false,
  items,
  isLoading,
  onCreate,
  onUpdate,
  onArchive,
  onRestore,
}: ReferenceFieldCatalogProps) {
  const [creatingDraft, setCreatingDraft] = useState<RowDraft | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<RowDraft | null>(null);
  const [busy, setBusy] = useState(false);

  const startCreate = () => {
    setEditingId(null);
    setEditDraft(null);
    setCreatingDraft(emptyDraft());
  };
  const startEdit = (it: FieldCatalogRow) => {
    setCreatingDraft(null);
    setEditingId(it.id);
    setEditDraft(rowToDraft(it));
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const run = async (fn: () => Promise<void>, after?: () => void) => {
    setBusy(true);
    try {
      await fn();
      after?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[field-catalog] действие не выполнено', err);
    } finally {
      setBusy(false);
    }
  };

  const submitCreate = () =>
    creatingDraft && draftValid(creatingDraft, true)
      ? run(
          () => onCreate(draftToPayload(creatingDraft, withRotation)),
          () => setCreatingDraft(null),
        )
      : undefined;

  const submitEdit = () => {
    if (!editingId || !editDraft || !draftValid(editDraft, false)) return undefined;
    const { code: _code, ...rest } = draftToPayload(editDraft, withRotation);
    return run(() => onUpdate(editingId, rest), cancelEdit);
  };

  const handleArchive = (it: FieldCatalogRow) => {
    if (!window.confirm(`Убрать «${it.name_translations.ru ?? it.code}» в архив?`)) return;
    void run(() => onArchive(it.id));
  };

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        <Button onClick={startCreate} disabled={creatingDraft !== null || busy}>
          <Plus className="h-4 w-4" />
          {createLabel}
        </Button>
      </div>

      <Card className="mt-6">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Название</TableHead>
                <TableHead style={{ width: '180px' }}>Код</TableHead>
                <TableHead style={{ width: '160px' }}>Цвет</TableHead>
                <TableHead style={{ width: '110px' }}>Порядок</TableHead>
                {withRotation && (
                  <TableHead style={{ width: '140px' }}>Севооборот</TableHead>
                )}
                <TableHead style={{ width: '170px' }}>Использование</TableHead>
                <TableHead style={{ width: '120px' }} className="text-right">
                  Действия
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {creatingDraft && (
                <TableRow className="bg-accent-brand/10">
                  <TableCell>
                    <Input
                      value={creatingDraft.ru}
                      onChange={(e) =>
                        setCreatingDraft({ ...creatingDraft, ru: e.target.value })
                      }
                      placeholder="название"
                      aria-label="Название"
                      className="h-8"
                      autoFocus
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={creatingDraft.code}
                      onChange={(e) =>
                        setCreatingDraft({
                          ...creatingDraft,
                          code: normalizeCatalogCode(e.target.value),
                        })
                      }
                      placeholder="latin_code"
                      aria-label="Код"
                      className="h-8 font-mono text-xs"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      потом не меняется
                    </p>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={creatingDraft.color}
                      onChange={(e) =>
                        setCreatingDraft({ ...creatingDraft, color: e.target.value })
                      }
                      aria-label="Цвет"
                      className="h-8 font-mono text-xs"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={creatingDraft.sortOrder}
                      onChange={(e) =>
                        setCreatingDraft({ ...creatingDraft, sortOrder: e.target.value })
                      }
                      aria-label="Порядок"
                      inputMode="numeric"
                      className="h-8"
                    />
                  </TableCell>
                  {withRotation && (
                    <TableCell>
                      <Switch
                        checked={creatingDraft.rotation}
                        onCheckedChange={(v) =>
                          setCreatingDraft({ ...creatingDraft, rotation: v })
                        }
                        aria-label="Севооборот"
                      />
                    </TableCell>
                  )}
                  <TableCell className="text-sm text-muted-foreground">—</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        onClick={submitCreate}
                        disabled={!draftValid(creatingDraft, true) || busy}
                        aria-label="Сохранить"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setCreatingDraft(null)}
                        disabled={busy}
                        aria-label="Отменить"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {isLoading && items.length === 0
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      <TableCell>
                        <Skeleton className="h-4 w-40" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-10" />
                      </TableCell>
                      {withRotation && (
                        <TableCell>
                          <Skeleton className="h-4 w-10" />
                        </TableCell>
                      )}
                      <TableCell>
                        <Skeleton className="h-4 w-16" />
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  ))
                : items.map((it) => {
                    const editing = editingId === it.id && editDraft;
                    if (editing) {
                      return (
                        <TableRow key={it.id} className="bg-accent-brand/10">
                          <TableCell>
                            <Input
                              value={editDraft.ru}
                              onChange={(e) =>
                                setEditDraft({ ...editDraft, ru: e.target.value })
                              }
                              aria-label="Название"
                              className="h-8"
                              autoFocus
                            />
                          </TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            {it.code}
                          </TableCell>
                          <TableCell>
                            <Input
                              value={editDraft.color}
                              onChange={(e) =>
                                setEditDraft({ ...editDraft, color: e.target.value })
                              }
                              aria-label="Цвет"
                              className="h-8 font-mono text-xs"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={editDraft.sortOrder}
                              onChange={(e) =>
                                setEditDraft({ ...editDraft, sortOrder: e.target.value })
                              }
                              aria-label="Порядок"
                              inputMode="numeric"
                              className="h-8"
                            />
                          </TableCell>
                          {withRotation && (
                            <TableCell>
                              <Switch
                                checked={editDraft.rotation}
                                onCheckedChange={(v) =>
                                  setEditDraft({ ...editDraft, rotation: v })
                                }
                                aria-label="Севооборот"
                              />
                            </TableCell>
                          )}
                          <TableCell className="text-sm tabular-nums text-muted-foreground">
                            {it.usage_count}
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                onClick={submitEdit}
                                disabled={!draftValid(editDraft, false) || busy}
                                aria-label="Сохранить"
                              >
                                <Check className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={cancelEdit}
                                disabled={busy}
                                aria-label="Отменить"
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    const inUse = it.usage_count > 0;
                    const archiveBlocked = it.is_system || inUse;
                    const archiveHint = it.is_system
                      ? 'Эта запись нужна системе для работы'
                      : inUse
                        ? `Используется: ${it.usage_count} ${usageUnit(it.usage_count)}`
                        : 'Убрать в архив';
                    return (
                      <TableRow key={it.id} className={cn(!it.is_active && 'opacity-60')}>
                        <TableCell className="font-medium">
                          <span className="flex flex-wrap items-center gap-2">
                            {it.name_translations.ru ?? it.code}
                            {!it.is_active && <Badge variant="secondary">В архиве</Badge>}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {it.code}
                        </TableCell>
                        <TableCell>
                          <ColorSwatch color={it.color} />
                        </TableCell>
                        <TableCell className="text-sm tabular-nums text-muted-foreground">
                          {it.sort_order}
                        </TableCell>
                        {withRotation && (
                          <TableCell className="text-sm text-muted-foreground">
                            {it.runs_crop_rotation ? 'Да' : 'Нет'}
                          </TableCell>
                        )}
                        <TableCell>
                          {inUse ? (
                            <Badge variant="secondary">
                              {it.usage_count} {usageUnit(it.usage_count)}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => startEdit(it)}
                              disabled={busy || creatingDraft !== null}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40"
                              aria-label="Редактировать"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            {it.is_active ? (
                              <button
                                type="button"
                                onClick={() => handleArchive(it)}
                                disabled={busy || archiveBlocked}
                                title={archiveHint}
                                aria-label={archiveHint}
                                className={cn(
                                  'inline-flex h-8 w-8 items-center justify-center rounded-md',
                                  archiveBlocked
                                    ? 'cursor-not-allowed text-muted-foreground/50'
                                    : 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive',
                                )}
                              >
                                {archiveBlocked ? (
                                  <Lock className="h-4 w-4" />
                                ) : (
                                  <Archive className="h-4 w-4" />
                                )}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => void run(() => onRestore(it.id))}
                                disabled={busy}
                                title="Вернуть из архива"
                                aria-label="Вернуть из архива"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40"
                              >
                                <ArchiveRestore className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </div>

        {!isLoading && items.length === 0 && !creatingDraft && (
          <div
            className="flex flex-col items-center gap-2 px-6 py-12 text-center"
            data-testid="field-catalog-empty"
          >
            <div className="text-sm font-medium text-foreground">Записей пока нет</div>
            <Button size="sm" onClick={startCreate} className="mt-2">
              <Plus className="h-4 w-4" />
              {createLabel}
            </Button>
          </div>
        )}
      </Card>
    </>
  );
}
